import React from "react";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import ErrorBoundary from "./components/ErrorBoundary";

import PrivateRoute from "./helpers/PrivateRoute";
import keycloak from "./config/keycloak";
import { BaseLayout } from "./layout/Base";
import { Home } from "./pages/Home";
import { DashboardPage } from "./pages/Dashboard";
import { SecurityLayout } from "./layout/Security";
import { Project } from "./pages/Project";
import { ProjectsPage } from "./pages/Projects";
import { ArchivedProjectsPage } from "./pages/ArchivedProjects";
import { AdminDownloadLogs } from "./pages/AdminDownloadLogs";
import { Room } from "./pages/Room";
import Documents from "./pages/Documents";
import Clients from "./pages/Clients";
import Tasks from "./pages/Tasks";
import { Contractors } from "./pages/Contractors";
import { Rack } from "./pages/Rack";
import { Marketplace } from "./pages/Marketplace";
import { Register } from "./pages/Register";
import { Login } from "./pages/Login";
import { Terms } from "./pages/Terms";
import { Policy } from "./pages/Policy";
import { Help } from "./pages/Help";
import { Privacy } from "./pages/Privacy";
import { Support } from "./pages/Support";
import { OurStory } from "./pages/OurStory";
import { Contacts } from "./pages/Contacts";
import Notifications from "./pages/Notifications";
import Profile from "./pages/Profile";
import UserProfile from "./pages/UserProfile";
import { Warranty } from "./pages/Warranty";
import { SitesMapPage } from "./pages/SitesMapPage";
import SessionManager from "./components/SessionManager";
// import { AIChat } from "./components/AIChat";
// import { useProjectsStore } from "./state/projects";

// AI Chat with full application context - Temporarily disabled
/*
const AIChatWithContext = () => {
  try {
    const location = useLocation();
    const { projects } = useProjectsStore();
    
    // Extract project ID from URL if we're on a project page
    const projectIdMatch = location.pathname.match(/\/projects\/([^\/]+)/);
    const currentProjectId = projectIdMatch ? projectIdMatch[1] : null;
    
    // Find current project data if available
    const currentProject = currentProjectId 
      ? projects.find((p: any) => p.id === currentProjectId)
      : null;
    
    // Debug logging for AI Chat context
    console.log('🤖 AI Chat Context Debug:', {
      projects: projects?.length || 0,
      currentPage: location.pathname,
      currentProject: currentProject?.name || 'None'
    });
    
    // Build comprehensive context
    const appContext = {
      // Current state
      currentPage: location.pathname,
      currentProject: currentProject,
      currentProjectId: currentProjectId,
      
      // Application data
      allProjects: projects,
      totalProjects: projects?.length || 0,
      activeProjects: projects?.filter((p: any) => p.status === 'active')?.length || 0,
      
      // Add status indicators
      projectsLoaded: Array.isArray(projects) && projects.length > 0,
      
      // Page detection
      isOnProjectPage: !!currentProjectId,
      isOnDashboard: location.pathname === '/' || location.pathname === '/dashboard',
      isOnMap: location.pathname === '/map',
      isOnProjects: location.pathname === '/projects',
      
      // User context (if available from keycloak or other auth)
      user: {
        // Add user data here when available
        authenticated: true
      },
      
      // Current route info
      routeInfo: {
        path: location.pathname,
        search: location.search,
        hash: location.hash
      }
    };
    
    return <AIChat contextData={appContext} />;
  } catch (error) {
    console.error('AIChatWithContext error:', error);
    return <AIChat contextData={undefined} />;
  }
};
*/

  // Keycloak event handlers — logging only.
  // Token refresh is handled centrally by SessionNetworkManager.
  // Doing updateToken() here would race with SessionNetworkManager and
  // invalidate the one-time-use refresh token, causing session loss.
const onKeycloakEvent = (event: string, error?: any) => {
  console.log('🔐 Keycloak event:', event, error);

  if (event === 'onReady') {
    console.log('✅ Keycloak is ready, authenticated:', keycloak.authenticated);
  }

  if (event === 'onInitError') {
    console.error('❌ Keycloak initialization error:', error);
  }

  if (event === 'onAuthError') {
    console.error('❌ Authentication error:', error);
  }

  if (event === 'onTokenExpired') {
    // SessionNetworkManager will handle the refresh.
    // Do NOT call keycloak.updateToken() here to avoid race conditions.
    console.log('🔄 Token expired — SessionNetworkManager will handle refresh');
  }

  if (event === 'onAuthRefreshError') {
    // Log only. SessionNetworkManager handles retry logic and eventual logout.
    console.error('❌ Token refresh error (will be handled by SessionNetworkManager):', error);
  }
};

const onKeycloakTokens = (tokens: any) => {
  console.log('🎫 Keycloak tokens updated:', {
    hasToken: !!tokens.token,
    hasRefreshToken: !!tokens.refreshToken,
    hasIdToken: !!tokens.idToken
  });
};

function App() {
  return (
          <ErrorBoundary>
      <ReactKeycloakProvider 
        initOptions={{ 
          onLoad: "check-sso",
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          checkLoginIframe: false, // Disable iframe checks to prevent issues
          enableLogging: true
        }} 
        authClient={keycloak}
        onEvent={onKeycloakEvent}
        onTokens={onKeycloakTokens}
      >
        <React.StrictMode>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={
                <BaseLayout><Home /></BaseLayout>
                }
              />
              <Route path="/terms" element={
                <BaseLayout><Terms /></BaseLayout>
                }
              />
              <Route path="/policy" element={
                <BaseLayout><Policy /></BaseLayout>
                }
              />
              <Route path="/our-story" element={
                <BaseLayout><OurStory /></BaseLayout>
                }
              />
              <Route path="/contacts" element={
                <BaseLayout><Contacts /></BaseLayout>
                }
              />
              <Route path="/help" element={
                <BaseLayout><Help /></BaseLayout>
                }
              />
              <Route path="/privacy" element={
                <BaseLayout><Privacy /></BaseLayout>
                }
              />
              <Route path="/support" element={
                <BaseLayout><Support /></BaseLayout>
                }
              />
              <Route path="/register" element={<Register />} />
              <Route path="/login" element={<Login />} />
              
              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <DashboardPage showMenu />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/map"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <DashboardPage showMenu />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sites-map"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <SitesMapPage />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/contractors"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Contractors />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/marketplace"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Marketplace />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/projects"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <ProjectsPage showMenu />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/archived"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <ArchivedProjectsPage showMenu />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/download-logs"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <AdminDownloadLogs />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Documents />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Clients />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Tasks />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Notifications />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Profile />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/users/:userId"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <UserProfile />
                    </SecurityLayout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/projects/:projectId"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Project />
                    </SecurityLayout>
                  </PrivateRoute>
                }  
              />
              <Route
                path="/projects/:projectId/warranties"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Warranty />
                    </SecurityLayout>
                  </PrivateRoute>
                }  
              />
              <Route
                path="/projects/:projectId/sites/:siteId/floors/:floorId/rooms/:roomId"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Room />
                    </SecurityLayout>
                  </PrivateRoute>
                }  
              />
              <Route
                path="/projects/:projectId/sites/:siteId/floors/:floorId/rooms/:roomId/rows/:rowId/racks/:rackId"
                element={
                  <PrivateRoute>
                    <SecurityLayout>
                      <Rack />
                    </SecurityLayout>
                  </PrivateRoute>
                }  
              />
            </Routes>
          </BrowserRouter>

      {/* AI Chat Assistant - Temporarily hidden */}
      {/* <AIChatWithContext /> */}

      {/* Session Manager - handles timeouts and inactivity */}
      <SessionManager />

      <ToastContainer hideProgressBar position="top-center" theme="colored" autoClose={1000} />
        </React.StrictMode>
      </ReactKeycloakProvider>
          </ErrorBoundary>
  );
}

export default App;
